/**
 * The analytics event vocabulary and — importantly — its **allowlist
 * serializers**.
 *
 * There is deliberately no `track(name, arbitraryPayload)`. Every event has a
 * typed input and a serializer that copies ONLY named, non-sensitive fields
 * onto the wire. That is what keeps emails, GitHub URLs, search strings, access
 * tokens, `Error.message`, stack traces and terminal output out of the request
 * body: a denylist would have to anticipate every leak, whereas an allowlist
 * fails closed on anything unexpected.
 *
 * Mirrors the server contract in ato-api `src/types/telemetry.ts`. Adding an
 * event here means adding it there too, or ingest answers 400.
 */
import type { LaunchOrigin } from "@/api/openCapsuleClient";

/**
 * Enumerated string values.
 *
 * These are VALIDATORS, not length limits. A "≤64 characters" rule is not a
 * PII control: an email address, a session token and a search phrase all fit
 * comfortably inside 64 characters, so a caller passing the wrong variable
 * would ship user data. An unknown value is dropped (or collapsed to
 * "unknown"), which fails closed.
 */

/** Surfaces a session can be presented on — mirrors SessionSurface's `kind`
 *  plus the terminal lane, which is not a SessionSurface variant. */
export const LAUNCH_SURFACES = ["web", "pixel_stream", "pixel", "terminal"] as const;
export type LaunchSurface = (typeof LAUNCH_SURFACES)[number];

/**
 * Where a run was initiated from.
 *
 * REUSES the existing launch vocabulary (`LaunchOrigin`, api/openCapsuleClient)
 * rather than inventing a parallel one — the origins already travel with every
 * launch, and a second list would silently disagree with it. Extra members are
 * discovery surfaces that never reach the launch API. The type assertion below
 * makes the containment a compile error rather than a runtime "unknown".
 */
export const RUN_ORIGINS = [
  // LaunchOrigin members
  "open",
  "run",
  "tile",
  "onboarding",
  "showcase",
  "billing_resume",
  // Discovery-only surfaces
  "search",
  "capsule_page",
  "library",
  "direct",
] as const;
export type RunOrigin = (typeof RUN_ORIGINS)[number];
/** Compile-time: every LaunchOrigin must be expressible as a RunOrigin. */
const _launchOriginIsRunOrigin: RunOrigin = "run" as LaunchOrigin;
void _launchOriginIsRunOrigin;

/** Where a search was issued from. */
export const SEARCH_SOURCES = ["palette", "home", "url"] as const;
export type SearchSource = (typeof SEARCH_SOURCES)[number];

/** Where a beta waitlist signup was submitted from — mirrors ato-api's
 *  `beta_waitlist_entries.source` CHECK constraint exactly (only these two
 *  values are accepted server-side). */
export const WAITLIST_PRODUCTS = ["cloud", "desktop"] as const;
export type WaitlistProduct = (typeof WAITLIST_PRODUCTS)[number];
export const WAITLIST_SOURCES = ["pwa_home", "pwa_desktop", "ato_web"] as const;
export type WaitlistSource = (typeof WAITLIST_SOURCES)[number];

/** Why a login CTA was shown or clicked. */
export const LOGIN_CTA_REASONS = [
  "session_expired",
  "run_requires_login",
  "save_app_requires_login",
  "snapshot_requires_login",
  "publish_requires_login",
  "continuation",
  // The signed-out landing page: its whole job is this conversion, and
  // attributing its CTAs to any of the reasons above would lose them inside
  // the run-funnel buckets they do not belong to.
  "landing",
] as const;
export type LoginCtaReason = (typeof LOGIN_CTA_REASONS)[number];

/** Which of the landing page's sign-in buttons was clicked. */
export const LOGIN_CTA_PLACEMENTS = ["header", "hero", "footer"] as const;
export type LoginCtaPlacement = (typeof LOGIN_CTA_PLACEMENTS)[number];

/**
 * Enumerated failure causes. An exception message is NEVER sent — it routinely
 * contains URLs, file paths and user input.
 */
export type FailureReason =
  | "capacity_unavailable"
  | "build_failed"
  | "restore_failed"
  | "readiness_timeout"
  | "unsupported_surface"
  | "auth_required"
  | "payment_required"
  | "network_error"
  // Rate limiting is a distinct, actionable cause — collapsing it into
  // "unknown" hides capacity pressure behind the same bucket as a genuine
  // mystery.
  | "rate_limited"
  // Bot-check (Turnstile) rejection — distinct from "unknown" so a spike in
  // failed signups from bot mitigation isn't confused with a real outage.
  | "bot_check_failed"
  // Client-side/server-side input validation rejection (e.g. malformed
  // email) — never carries the offending value, only that validation failed.
  | "validation_error"
  | "unknown";

export const FAILURE_REASONS: readonly FailureReason[] = [
  "capacity_unavailable",
  "build_failed",
  "restore_failed",
  "readiness_timeout",
  "unsupported_surface",
  "auth_required",
  "payment_required",
  "network_error",
  "rate_limited",
  "bot_check_failed",
  "validation_error",
  "unknown",
] as const;

/**
 * The launch lane's blocker vocabulary (`LaunchBlocker`, api/openCapsuleClient)
 * mapped onto the analytics reasons. Two vocabularies exist because one
 * describes what the API refused and the other groups causes for reporting;
 * without this mapping every blocker arrived as an unrecognised string and
 * collapsed to "unknown", which is exactly what the breakdown must not do.
 */
const BLOCKER_TO_REASON: Record<string, FailureReason> = {
  unsupported: "unsupported_surface",
  not_found: "unsupported_surface",
  conflict: "capacity_unavailable",
  rate_limited: "rate_limited",
  timeout: "readiness_timeout",
  auth_required: "auth_required",
  payment_required: "payment_required",
};

/** Coerce anything into a known reason code — unknown strings collapse to
 *  "unknown" rather than travelling as free text. */
export function toFailureReason(value: unknown): FailureReason {
  if (FAILURE_REASONS.includes(value as FailureReason)) return value as FailureReason;
  if (typeof value === "string" && BLOCKER_TO_REASON[value]) {
    return BLOCKER_TO_REASON[value];
  }
  return "unknown";
}

/** Membership test for one of the enums above. */
function enumOf<T extends string>(
  allowed: readonly T[],
): (value: unknown) => T | undefined {
  const set = new Set<string>(allowed);
  return (value: unknown) =>
    typeof value === "string" && set.has(value) ? (value as T) : undefined;
}

/** Exported coercers, so the legacy adapter can narrow free-text values at the
 *  boundary instead of casting past the type system. */
export const toRunOrigin = enumOf(RUN_ORIGINS);
export const toLaunchSurface = enumOf(LAUNCH_SURFACES);
export const toSearchSource = enumOf(SEARCH_SOURCES);
export const toLoginCtaReason = enumOf(LOGIN_CTA_REASONS);

const runOrigin = enumOf(RUN_ORIGINS);
const launchSurface = enumOf(LAUNCH_SURFACES);
const searchSource = enumOf(SEARCH_SOURCES);
const loginCtaReason = enumOf(LOGIN_CTA_REASONS);
const loginCtaPlacement = enumOf(LOGIN_CTA_PLACEMENTS);
const waitlistSource = enumOf(WAITLIST_SOURCES);
const waitlistProduct = enumOf(WAITLIST_PRODUCTS);

/**
 * What identifies a capsule on the wire.
 *
 * `capsuleId` is the DATABASE id (`CapsuleSummary.id`, a ULID) — the column the
 * admin dashboard joins `capsules` on. It is deliberately NOT the slug and NOT
 * the `publisher/slug` ref: the browser holds all three, and sending whichever
 * one a call site happened to have split one capsule into three groups, lost
 * its name in the top-capsule table, and 404'd the capsule filter.
 *
 * `capsuleRef` is the human "publisher/slug", carried ALONGSIDE the id. Some
 * legacy funnel paths only ever have the ref; ingest resolves it to the same
 * id, so an event is attributed either way.
 */
export interface CapsuleScoped {
  /** `capsules.id` (ULID). */
  capsuleId?: string;
  /** "publisher/slug". Never a substitute for a known id. */
  capsuleRef?: string;
}

/**
 * A capsule-scoped event's subject: at least ONE of the two names, enforced by
 * the type rather than left to the call site.
 *
 * Both-optional let a caller emit an event with no subject at all. Ingest
 * rejects that (400), and because the client treats 4xx as "this batch is
 * malformed, do not retry", one such event discarded the whole batch — taking
 * every valid event beside it.
 */
export type CapsuleSubject =
  | { capsuleId: string; capsuleRef?: string }
  | { capsuleId?: undefined; capsuleRef: string };

/** Narrow two possibly-missing names into a subject, or null when neither is
 *  usable. The one place a raw pair becomes a valid event input. */
export function capsuleSubject(
  capsuleId: string | undefined,
  capsuleRef: string | undefined,
): CapsuleSubject | null {
  if (capsuleId) return { capsuleId, capsuleRef };
  if (capsuleRef) return { capsuleRef };
  return null;
}

/** Typed input per event. `capsuleId` / `capsuleRef` / `launchId` are lifted
 *  out of the payload into top-level wire columns by the client. */
export interface AnalyticsEventMap {
  // ── Discovery ──
  app_opened: Record<string, never>;
  capsule_viewed: CapsuleSubject & { origin?: RunOrigin };
  /** The query STRING is never included — only its shape. */
  search_submitted: { resultCount: number; source?: SearchSource; filtersCount?: number };
  search_result_clicked: CapsuleSubject & { position?: number };
  preview_impression: CapsuleSubject;
  preview_dwell_ms: CapsuleSubject & { ms: number };

  // ── Run ──
  run_click: CapsuleSubject & { origin?: RunOrigin };
  launch_started: CapsuleSubject & { launchId?: string };
  launch_ready: CapsuleSubject & {
    launchId?: string;
    ms?: number;
    surface?: LaunchSurface;
    coldStart?: boolean | null;
    snapshotUsed?: boolean | null;
    origin?: RunOrigin;
  };
  launch_failed: CapsuleSubject & { launchId?: string; reason: FailureReason };
  live_session_started: CapsuleSubject & { launchId?: string; surface?: LaunchSurface };
  session_expired: CapsuleSubject & { launchId?: string };
  stop_clicked: CapsuleSubject & { launchId?: string };
  retry_clicked: CapsuleSubject & { launchId?: string };

  // ── Engagement ──
  active_window_seconds: CapsuleSubject & { seconds: number };

  // ── Auth ──
  // `placement` distinguishes the landing page's several sign-in buttons
  // (header / hero / footer) — the "which copy converts" split, folded into
  // the existing auth-funnel events rather than minting landing-specific
  // kinds that would need an ato-api contract change to ingest.
  login_cta_shown: CapsuleScoped & {
    reason?: LoginCtaReason;
    placement?: LoginCtaPlacement;
  };
  login_cta_clicked: CapsuleScoped & {
    reason?: LoginCtaReason;
    placement?: LoginCtaPlacement;
  };
  login_completed: Record<string, never>;

  // ── Snapshot / import / publish ──
  snapshot_created: CapsuleSubject;
  snapshot_restored: CapsuleSubject;
  snapshot_failed: CapsuleSubject & { reason: FailureReason };
  // Import and publish are GLOBAL: both fire while a repo is being turned into
  // a capsule, which is precisely the window in which no capsule id exists yet.
  // `capsuleId` stays optional so a re-publish of an existing capsule can carry
  // one.
  repo_import_started: CapsuleScoped;
  repo_import_completed: CapsuleScoped;
  repo_import_failed: CapsuleScoped & { reason: FailureReason };
  publish_request_started: CapsuleScoped;
  publish_request_submitted: CapsuleScoped;
  publish_request_failed: CapsuleScoped & { reason: FailureReason };

  // ── Misc ──
  share_click: CapsuleSubject;
  pwa_installed: Record<string, never>;

  // ── Beta waitlist (Home CTA + modal) ──
  // No PII field exists on any of these — see serializePayload below, which
  // only ever copies `source` / `reason_code` onto the wire.
  waitlist_opened: { product: WaitlistProduct; source: WaitlistSource };
  waitlist_submitted: { product: WaitlistProduct; source: WaitlistSource };
  waitlist_succeeded: { product: WaitlistProduct; source: WaitlistSource };
  waitlist_failed: {
    product: WaitlistProduct;
    source: WaitlistSource;
    reason: FailureReason;
  };
}

export type AnalyticsEventName = keyof AnalyticsEventMap;

/** Events with no capsule — must match ato-api's TELEMETRY_GLOBAL_EVENTS. */
export const GLOBAL_EVENTS: ReadonlySet<AnalyticsEventName> = new Set([
  "app_opened",
  "search_submitted",
  "login_cta_shown",
  "login_cta_clicked",
  "login_completed",
  "pwa_installed",
  "repo_import_started",
  "repo_import_completed",
  "repo_import_failed",
  "publish_request_started",
  "publish_request_submitted",
  "publish_request_failed",
  "waitlist_opened",
  "waitlist_submitted",
  "waitlist_succeeded",
  "waitlist_failed",
]);

/** Events that MUST name a capsule — the complement of GLOBAL_EVENTS. Used by
 *  the client's runtime guard as well as the types. */
export function isCapsuleScopedEvent(name: AnalyticsEventName): boolean {
  return !GLOBAL_EVENTS.has(name);
}

/** Flushed the moment they are recorded: each marks the end of a funnel the
 *  user may navigate away from immediately after. */
export const IMMEDIATE_FLUSH_EVENTS: ReadonlySet<AnalyticsEventName> = new Set([
  "launch_ready",
  "launch_failed",
  "publish_request_submitted",
]);

const num = (v: unknown): number | undefined =>
  typeof v === "number" && Number.isFinite(v) ? v : undefined;
const bool = (v: unknown): boolean | undefined =>
  typeof v === "boolean" ? v : undefined;

/** Drop undefined keys so the wire payload stays minimal and stable. */
function compact(o: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(o)) if (v !== undefined) out[k] = v;
  return out;
}

/**
 * Per-event allowlist serializer. Anything not named here — including extra
 * properties a caller passes by mistake — never reaches the network.
 */
export function serializePayload<K extends AnalyticsEventName>(
  name: K,
  input: AnalyticsEventMap[K],
): Record<string, unknown> {
  const p = input as Record<string, unknown>;
  switch (name) {
    case "search_submitted":
      // Deliberately NOT the query text.
      return compact({
        result_count: num(p.resultCount),
        source: searchSource(p.source),
        filters_count: num(p.filtersCount),
      });
    case "search_result_clicked":
      return compact({ position: num(p.position) });
    case "capsule_viewed":
    case "run_click":
      return compact({ origin: runOrigin(p.origin) });
    case "preview_dwell_ms":
      return compact({ ms: num(p.ms) });
    case "launch_ready":
      return compact({
        ms: num(p.ms),
        surface: launchSurface(p.surface),
        cold_start: bool(p.coldStart) ?? null,
        snapshot_used: bool(p.snapshotUsed) ?? null,
        origin: runOrigin(p.origin),
      });
    case "launch_failed":
    case "snapshot_failed":
    case "repo_import_failed":
    case "publish_request_failed":
      // Reason code only — never an exception message.
      return { reason_code: toFailureReason(p.reason) };
    case "live_session_started":
      return compact({ surface: launchSurface(p.surface) });
    case "active_window_seconds":
      return compact({ seconds: num(p.seconds) });
    case "login_cta_shown":
    case "login_cta_clicked":
      return compact({
        reason: loginCtaReason(p.reason),
        placement: loginCtaPlacement(p.placement),
      });
    case "waitlist_opened":
    case "waitlist_submitted":
    case "waitlist_succeeded":
      return compact({
        product: waitlistProduct(p.product),
        source: waitlistSource(p.source),
      });
    case "waitlist_failed":
      return compact({
        product: waitlistProduct(p.product),
        source: waitlistSource(p.source),
        reason_code: toFailureReason(p.reason),
      });
    default:
      // Events whose meaning is fully carried by name + capsule/launch id.
      return {};
  }
}

/**
 * The capsule an event is about, if any. Lifted to a wire column.
 *
 * GLOBAL_EVENTS says a capsule is not REQUIRED, not that one must be withheld
 * — a re-publish of an existing capsule knows its id and there is no reason to
 * drop it.
 */
export function extractCapsuleId<K extends AnalyticsEventName>(
  _name: K,
  input: AnalyticsEventMap[K],
): string | undefined {
  const v = (input as Record<string, unknown>).capsuleId;
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

/** The "publisher/slug" ref an event carries, if any. Never a substitute for
 *  `capsule_id`: ingest resolves it to the id and stores BOTH. */
export function extractCapsuleRef<K extends AnalyticsEventName>(
  input: AnalyticsEventMap[K],
): string | undefined {
  const v = (input as Record<string, unknown>).capsuleRef;
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

/** The launch an event belongs to, if any. */
export function extractLaunchId<K extends AnalyticsEventName>(
  input: AnalyticsEventMap[K],
): string | undefined {
  const v = (input as Record<string, unknown>).launchId;
  return typeof v === "string" && v.length > 0 ? v : undefined;
}
